import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Target, Award, Users, TrendingUp, Lock, FileCheck, Search, Eye, Lightbulb, ArrowRight } from 'lucide-react';

const AboutPage = () => {
  const experience = [
    { icon: FileCheck, title: 'ISO 27001', description: 'Gap analysis, implementação e certificação' },
    { icon: Shield, title: 'PPSI', description: 'Programa de Proteção e Segurança da Informação' },
    { icon: Lock, title: 'Programas de Privacidade', description: 'LGPD, GDPR e outras regulamentações' },
    { icon: TrendingUp, title: 'Gestão de Riscos', description: 'Corporativos e tecnológicos' },
    { icon: Search, title: 'Forense Digital', description: 'Investigação de incidentes e evidências' },
  ];

  const values = [
    {
      icon: Shield,
      title: 'Confidencialidade',
      description: 'Proteção absoluta das informações estratégicas e sensíveis dos nossos clientes.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Award,
      title: 'Ética',
      description: 'Conduta profissional pautada em transparência, integridade e responsabilidade.',
      color: 'from-emerald-500 to-teal-500'
    },
    {
      icon: Target,
      title: 'Foco em Resultados',
      description: 'Soluções práticas que geram valor mensurável para o negócio.',
      color: 'from-orange-500 to-amber-500'
    },
    {
      icon: Users,
      title: 'Parceria de Longo Prazo',
      description: 'Relacionamentos duradouros baseados em confiança mútua e crescimento conjunto.',
      color: 'from-violet-500 to-purple-500'
    }
  ];

  const differentials = [
    {
      icon: Lightbulb,
      title: 'Abordagem Integrada',
      description: 'Combinamos consultoria especializada com a plataforma proprietária SmartAudit, oferecendo visão holística e ferramentas práticas de gestão.'
    },
    {
      icon: Eye,
      title: 'Visão Executiva',
      description: 'Dashboards e KPIs que traduzem complexidade técnica em indicadores estratégicos para tomada de decisão.'
    },
    {
      icon: Shield,
      title: 'Experiência Prática',
      description: 'Time com vivência real em forense digital, resposta a incidentes e testes de penetração, não apenas teoria.'
    },
    {
      icon: Users,
      title: 'Metodologia Consultiva',
      description: 'Trabalhamos lado a lado com sua equipe, capacitando e transferindo conhecimento para sustentabilidade.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Sobre Nós - DMM Security | Consultoria em Segurança da Informação</title>
        <meta name="description" content="Conheça a DMM Security, seu advisor estratégico em segurança da informação, governança, risco e compliance. Parceria de longo prazo com foco em resultados." />
      </Helmet>

      <div className="min-h-screen pt-20" data-testid="about-page">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-[#0f2535] via-[#1a3a52] to-[#1e4d6b] text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `linear-gradient(rgba(16,185,129,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.2) 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }} />
          </div>
          
          <div className="container mx-auto px-4 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <span className="inline-block px-4 py-1.5 bg-[#10b981]/20 text-[#10b981] text-sm font-semibold rounded-full mb-6">
                Sobre a DMM
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Seu Advisor Estratégico em Segurança
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
                Elevando a maturidade de segurança da informação, governança, risco e compliance das organizações
              </p>
            </motion.div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 bg-white" data-testid="mission-section">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a52] mb-8">Nosso Posicionamento</h2>
                <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                  <p>
                    A <strong className="text-[#1a3a52]">DMM Soluções</strong> atua como <strong className="text-[#10b981]">parceiro estratégico</strong> de 
                    médias e grandes organizações que buscam elevar sua maturidade em segurança da informação, 
                    governança, riscos e compliance (GRC).
                  </p>
                  <p>
                    Não somos apenas executores de projetos pontuais. Somos <strong className="text-[#1a3a52]">advisors</strong> que 
                    compreendem os desafios estratégicos dos nossos clientes e trabalham em conjunto para construir 
                    programas sustentáveis de segurança e conformidade.
                  </p>
                  <p>
                    Nossa abordagem combina <strong className="text-[#1a3a52]">experiência prática</strong> em projetos complexos 
                    com ferramentas tecnológicas próprias, como a plataforma <strong className="text-[#10b981]">SmartAudit</strong>, 
                    que consolida frameworks, automatiza cálculos de risco e fornece visibilidade executiva através de 
                    dashboards e KPIs.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Experience Section */}
        <section className="py-20 bg-gray-50" data-testid="experience-section">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-center mb-12"
              >
                <span className="inline-block px-4 py-1.5 bg-[#1a3a52]/10 text-[#1a3a52] text-sm font-semibold rounded-full mb-4">
                  Expertise
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a52]">Áreas de Experiência</h2>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {experience.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-[#10b981]/30 group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#1a3a52]/10 flex items-center justify-center mb-4 group-hover:bg-[#10b981] transition-colors">
                      <item.icon className="w-6 h-6 text-[#1a3a52] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-xl font-bold text-[#1a3a52] mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 bg-white" data-testid="values-section">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <span className="inline-block px-4 py-1.5 bg-[#10b981]/10 text-[#10b981] text-sm font-semibold rounded-full mb-4">
                Nossos Valores
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a52] mb-4">
                Princípios que nos Guiam
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Valores que norteiam todas as nossas ações e relacionamentos
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center group"
                >
                  <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${value.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <value.icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1a3a52] mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Differentials Section */}
        <section className="py-20 bg-gray-50" data-testid="differentials-section">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <span className="inline-block px-4 py-1.5 bg-[#1a3a52]/10 text-[#1a3a52] text-sm font-semibold rounded-full mb-4">
                Diferenciais
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a52] mb-4">
                O Que Nos Torna Únicos
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {differentials.map((differential, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#10b981]/30"
                >
                  <div className="flex items-start gap-5">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#1a3a52] to-[#10b981] flex items-center justify-center flex-shrink-0">
                      <differential.icon className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#1a3a52] mb-3">{differential.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{differential.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-br from-[#1a3a52] to-[#10b981] text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 30% 70%, rgba(255,255,255,0.1) 0%, transparent 50%)`
            }} />
          </div>
          
          <div className="container mx-auto px-4 text-center relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Vamos Construir uma Parceria de Longo Prazo?
              </h2>
              <p className="text-xl mb-10 text-gray-200">
                Entre em contato e descubra como podemos apoiar sua jornada de maturidade em segurança e compliance
              </p>
              <Link
                to="/contato"
                className="group inline-flex items-center gap-2 bg-white text-[#1a3a52] px-10 py-5 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-2xl hover:scale-105"
                data-testid="about-cta-btn"
              >
                Agendar Conversa
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AboutPage;
